const EDIT_MODE_STORAGE_KEY = 'edit_mode';

let navigationStarted = false;
const listeners = new Set<(started: boolean) => void>();

/**
 * Clears the in-page lock when Chrome restores this document from bfcache.
 * A normal back navigation reloads the bundle; bfcache thaws the frozen heap, so
 * without this the pencil stays disabled until a full reload.
 */
if (typeof window !== 'undefined') {
    window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        navigationStarted = false;
        listeners.forEach((listener) => listener(false));
    });
}

/**
 * Starts a single document navigation to a Business Manager edit URL.
 *
 * A second call is ignored so a retry click cannot abort the in-flight document request.
 */
export function startEditModeNavigation(editLink: string): void {
    if (navigationStarted || !editLink) {
        return;
    }

    try {
        sessionStorage.setItem(
            EDIT_MODE_STORAGE_KEY,
            JSON.stringify({
                value: editLink,
                expiry: Date.now(),
            }),
        );
    } catch {
        // Edit-mode restore after navigation is best-effort when storage is blocked.
    }

    navigationStarted = true;
    listeners.forEach((listener) => listener(true));
    window.location.href = editLink;
}

/** Whether an edit-mode document navigation has already started in this page. */
export function isEditModeNavigationStarted(): boolean {
    return navigationStarted;
}

/** Subscribes to the in-page edit-mode navigation lock. */
export function subscribeEditModeNavigation(
    listener: (started: boolean) => void,
): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** Test-only reset for the in-page navigation lock. */
export function resetEditModeNavigationForTests(): void {
    navigationStarted = false;
    listeners.clear();
}
